import React from 'react';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';

/**
 * Einstellungen — Layout-Primitive.
 *
 * Bewusst kartenlos: Themenblöcke werden über Label + Haarlinie getrennt,
 * nicht über verschachtelte Karten. Alles Visuelle steckt in settings.css
 * (`--st-*`), damit es genau einen Tokensatz gibt.
 */

type IconType = React.ComponentType<{ className?: string }>;

/* ------------------------------------------------------------------ Head */

interface SettingsHeadProps {
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
}

export function SettingsHead({ title, description, action }: SettingsHeadProps) {
  return (
    <header className="st-head">
      <div className="st-head__copy">
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {action && <div className="st-head__action">{action}</div>}
    </header>
  );
}

/* ----------------------------------------------------------------- Group */

interface SettingsGroupProps {
  label?: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

export function SettingsGroup({ label, description, action, className, children }: SettingsGroupProps) {
  return (
    <section className={cn('st-group', className)}>
      {(label || action) && (
        <div className="st-group__head">
          {label && <h2 className="st-group__label">{label}</h2>}
          {action}
        </div>
      )}
      {description && <p className="st-group__desc">{description}</p>}
      {children}
    </section>
  );
}

/* ------------------------------------------------------------------- Row */

interface SettingsRowProps {
  label: React.ReactNode;
  description?: React.ReactNode;
  htmlFor?: string;
  /** Steuerelement über die volle Breite unter den Text legen (Textarea, Tabelle, Code). */
  stack?: boolean;
  tone?: 'default' | 'danger';
  className?: string;
  children?: React.ReactNode;
}

export function SettingsRow({
  label,
  description,
  htmlFor,
  stack,
  tone = 'default',
  className,
  children,
}: SettingsRowProps) {
  return (
    <div
      className={cn('st-row', stack && 'st-row--stack', tone === 'danger' && 'st-row--danger', className)}
    >
      <div className="st-row__copy">
        {htmlFor ? (
          <label htmlFor={htmlFor} className="st-row__label">{label}</label>
        ) : (
          <span className="st-row__label">{label}</span>
        )}
        {description && <p className="st-row__desc">{description}</p>}
      </div>
      {children && <div className="st-row__control">{children}</div>}
    </div>
  );
}

/* ---------------------------------------------------------------- Fields */

export function SettingsFields({
  columns = 2,
  className,
  children,
}: {
  columns?: 1 | 2;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('st-fields', columns === 1 && 'st-fields--single', className)}>{children}</div>
  );
}

interface SettingsFieldProps {
  label: React.ReactNode;
  htmlFor?: string;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

export function SettingsField({ label, htmlFor, hint, error, className, children }: SettingsFieldProps) {
  return (
    <div className={cn('st-field', className)}>
      {/* Ohne htmlFor kein <label> — sonst landen Buttons (z. B. der
          Platzhalter-Picker) in einem Label und lösen dessen Klick aus. */}
      {htmlFor ? <label htmlFor={htmlFor}>{label}</label> : <div className="st-field__label">{label}</div>}
      {children}
      {error ? <p className="st-field__error">{error}</p> : hint ? <p className="st-field__hint">{hint}</p> : null}
    </div>
  );
}

/* --------------------------------------------------------------- Toolbar */

export function SettingsToolbar({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn('st-toolbar', className)}>{children}</div>;
}

export function SettingsToolbarSpacer() {
  return <span className="st-toolbar__spacer" />;
}

/* ----------------------------------------------------------------- Table */

/** Randlose Tabelle, die bis an die Seitenkante läuft — wie die Ticketliste. */
export function SettingsTable({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn('st-table', className)}>{children}</div>;
}

/* ----------------------------------------------------------------- Empty */

interface SettingsEmptyProps {
  icon?: IconType;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
}

export function SettingsEmpty({ icon: Icon, title, description, action }: SettingsEmptyProps) {
  return (
    <div className="st-empty">
      {Icon && (
        <div className="st-empty__icon">
          <Icon className="h-4 w-4" />
        </div>
      )}
      <h3>{title}</h3>
      {description && <p>{description}</p>}
      {action && <div className="st-empty__action">{action}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ Note */

export function SettingsNote({
  tone = 'default',
  icon: Icon,
  className,
  children,
}: {
  tone?: 'default' | 'warning' | 'danger' | 'success';
  icon?: IconType;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('st-note', className)} data-tone={tone}>
      {Icon && <Icon className="h-3.5 w-3.5" />}
      <div>{children}</div>
    </div>
  );
}

/* ----------------------------------------------------------------- Badge */

export function SettingsBadge({
  tone = 'default',
  children,
}: {
  tone?: 'default' | 'success' | 'danger' | 'accent';
  children: React.ReactNode;
}) {
  return (
    <span className="st-badge" data-tone={tone}>
      {children}
    </span>
  );
}

/* ----------------------------------------------------------------- Sheet */

interface SettingsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  footer?: React.ReactNode;
  width?: number;
  children: React.ReactNode;
}

/**
 * Rechter Editor. Nutzt das echte Sheet-Primitiv — der alte Aufbau presste
 * einen zentrierten Dialog mit ~115 Zeilen !important in eine Drawer-Form.
 */
export function SettingsSheet({
  open,
  onOpenChange,
  title,
  description,
  footer,
  width = 620,
  children,
}: SettingsSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="st-shell st-sheet w-full gap-0 overflow-hidden p-0 sm:max-w-none"
        style={{ width: `min(${width}px, 100vw)`, maxWidth: `${width}px`, height: '100dvh' }}
      >
        <div className="st-sheet__head">
          <div className="min-w-0">
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription className={description ? undefined : 'sr-only'}>
              {description ?? title}
            </SheetDescription>
          </div>
        </div>

        <div className="st-sheet__body st-main">{children}</div>

        {footer && <div className="st-sheet__foot">{footer}</div>}
      </SheetContent>
    </Sheet>
  );
}

/* -------------------------------------------------------------- Skeleton */

export function SettingsSkeleton() {
  return (
    <div className="st-skeleton" aria-hidden="true">
      <span />
      <span />
      <span />
      <span />
    </div>
  );
}
